import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IMessage } from "../types/MessageTypes";

interface IMessageState {
  activeChatId: number | null;
  activeChatTelegramId: string | null;
  activeChatWidgetSessionId: string | null;
  messages: { [ticketId: number]: IMessage[] };
  unreadMessages: { [ticketId: number]: number };
  isLoading: boolean;
  error: string;
}

const initialState: IMessageState = {
  activeChatId: null,
  activeChatTelegramId: null,
  activeChatWidgetSessionId: null,
  messages: {},
  unreadMessages: {},
  error: "",
  isLoading: false,
};

export const messageSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    setActiveChatId(
      state,
      action: PayloadAction<{
        ticketId: number;
        telegramId?: string | null;
        widgetSessionId?: string | null;
      }>,
    ) {
      const {
        ticketId,
        telegramId = null,
        widgetSessionId = null,
      } = action.payload;

      state.activeChatId = ticketId;
      state.activeChatTelegramId = telegramId;
      state.activeChatWidgetSessionId = widgetSessionId;

      state.unreadMessages[ticketId] = 0;
    },

    resetActiveChatId(state) {
      state.activeChatId = null;
      state.activeChatTelegramId = null;
      state.activeChatWidgetSessionId = null;
    },

    getMessagesByOperatorIdLoading(state) {
      state.isLoading = true;
      state.error = "";
    },

    getMessagesByOperatorIdSuccess(state, action: PayloadAction<IMessage[]>) {
      state.messages = {};
      state.unreadMessages = {};

      for (const msg of action.payload) {
        const ticketId = msg.ticketId;

        if (!state.messages[ticketId]) {
          state.messages[ticketId] = [];
        }

        state.messages[ticketId].push(msg);

        if (state.unreadMessages[ticketId] === undefined) {
          state.unreadMessages[ticketId] = 0;
        }
      }

      state.isLoading = false;
      state.error = "";
    },

    getMessagesByOperatorIdFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getMessagesByTicketIdLoading(state) {
      state.isLoading = true;
      state.error = "";
    },

    getMessagesByTicketIdSuccess(state, action: PayloadAction<IMessage[]>) {
      if (action.payload.length === 0) {
        state.isLoading = false;
        return;
      }

      const ticketId = action.payload[0].ticketId;

      state.messages[ticketId] = [...action.payload];
      state.unreadMessages[ticketId] = 0;

      state.isLoading = false;
    },

    getMessagesByTicketIdFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    addMessage(state, action: PayloadAction<IMessage>) {
      const msg = action.payload;
      const ticketId = msg.ticketId;

      if (!ticketId) return;

      if (!state.messages[ticketId]) {
        state.messages[ticketId] = [];
      }

      const exists = state.messages[ticketId].some((m) => m.id === msg.id);
      if (exists) return;

      state.messages[ticketId].push(msg);

      const isUserMessage = !msg.operatorId;

      if (isUserMessage) {
        if (state.activeChatId !== ticketId) {
          state.unreadMessages[ticketId] =
            (state.unreadMessages[ticketId] ?? 0) + 1;
        } else {
          state.unreadMessages[ticketId] = 0;
        }
      }
    },

    removeMessages(state, action: PayloadAction<number>) {
      const ticketId = action.payload;

      delete state.messages[ticketId];
      delete state.unreadMessages[ticketId];
    },
  },
});

export default messageSlice.reducer;
